import React, { useState, useEffect, useCallback } from 'react';
import { httpAdmin } from '../lib/http';
import {
  useGuard401,
  ConfirmationDialog,
  PaginationBar,
  useDebouncedValue,
} from './adminShared';
import './NotificationManagement.css';

const EMPTY_FORM = {
  title: '',
  message: '',
  targetType: 'all',
  targetUsers: [],
  targetBusinesses: [],
  channels: { email: false, sms: false, push: false, inApp: true },
  sendImmediately: true,
  scheduledAt: '',
  template: 'custom',
  priority: 'normal',
  tags: [],
};

const TEMPLATE_CONTENT = {
  booking_reminder: { title: 'Booking reminder', message: 'This is a reminder about your upcoming booking.' },
  promotion: { title: 'A special offer for you', message: 'Discover our latest offer and book your next appointment.' },
  system_update: { title: 'Service update', message: 'We have an important update to share with you.' },
  welcome: { title: 'Welcome to Ehgezly', message: 'We are happy to have you with us.' },
  password_reset: { title: 'Password reset request', message: 'Use the password reset link to secure your account.' },
};

const selectedLabel = (item) => item?.name || item?.title || item?.email || String(item?._id || item);
const selectedId = (item) => String(item?._id || item);

const NotificationManagement = () => {
  const guard = useGuard401();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [editingNotification, setEditingNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [stats, setStats] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [formError, setFormError] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [audienceSearch, setAudienceSearch] = useState('');
  const [audienceResults, setAudienceResults] = useState([]);
  const [audienceLabels, setAudienceLabels] = useState({});

  const closeAllModals = () => {
    setShowCreateModal(false);
    setSelectedNotification(null);
    setEditingNotification(null);
    setFormError('');
    setAudienceSearch('');
    setAudienceResults([]);
    setAudienceLabels({});
  };

  const [filters, setFilters] = useState({
    status: '',
    targetType: '',
    page: 1,
    limit: 20,
  });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const debouncedAudienceSearch = useDebouncedValue(audienceSearch, 300);

  useEffect(() => {
    setFilters((f) => (f.page === 1 ? f : { ...f, page: 1 }));
  }, [debouncedSearch]);

  useEffect(() => {
    setExpandedId(null);
  }, [debouncedSearch, filters.status, filters.targetType, filters.page]);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      Object.entries({ ...filters, search: debouncedSearch }).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await httpAdmin.getJson(`/api/admin/notifications?${params}`);
      setNotifications(response.notifications || []);
      setTotal(Number(response.total) || 0);
    } catch (err) {
      if (guard(err)) return;
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [filters, debouncedSearch, guard]);

  const loadStats = useCallback(async () => {
    try {
      const response = await httpAdmin.getJson('/api/admin/notifications/stats?period=30d');
      setStats(response);
    } catch (err) {
      if (!guard(err)) console.error('Load stats error:', err);
    }
  }, [guard]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const targetType = createForm.targetType;
    if (!['specific_users', 'specific_businesses'].includes(targetType) || !debouncedAudienceSearch.trim()) {
      setAudienceResults([]);
      return undefined;
    }

    const loadAudience = async () => {
      try {
        const endpoint = targetType === 'specific_users' ? '/api/admin/users' : '/api/admin/providers';
        const response = await httpAdmin.getJson(
          `${endpoint}?q=${encodeURIComponent(debouncedAudienceSearch.trim())}&limit=20${targetType === 'specific_businesses' ? '&state=all' : ''}`,
        );
        setAudienceResults(response.data || response.users || []);
      } catch (err) {
        if (!guard(err)) setAudienceResults([]);
      }
    };
    loadAudience();
    return undefined;
  }, [createForm.targetType, debouncedAudienceSearch, guard]);

  const openCreateModal = () => {
    setCreateForm(EMPTY_FORM);
    setTagInput('');
    setAudienceLabels({});
    setEditingNotification(null);
    setFormError('');
    setShowCreateModal(true);
  };

  const openEditModal = (notification) => {
    setCreateForm({
      ...EMPTY_FORM,
      ...notification,
      targetUsers: (notification.targetUsers || []).map(selectedId),
      targetBusinesses: (notification.targetBusinesses || []).map(selectedId),
      channels: { ...EMPTY_FORM.channels, ...(notification.channels || {}) },
      sendImmediately: notification.status === 'sending' ? true : !notification.scheduledAt,
      scheduledAt: notification.scheduledAt ? new Date(notification.scheduledAt).toISOString().slice(0, 16) : '',
      tags: notification.tags || [],
    });
    setTagInput('');
    setAudienceLabels(
      [...(notification.targetUsers || []), ...(notification.targetBusinesses || [])].reduce(
        (labels, item) => ({ ...labels, [selectedId(item)]: selectedLabel(item) }),
        {},
      ),
    );
    setEditingNotification(notification);
    setSelectedNotification(null);
    setFormError('');
    setShowCreateModal(true);
  };

  const addAudienceItem = (item) => {
    const key = createForm.targetType === 'specific_users' ? 'targetUsers' : 'targetBusinesses';
    const id = selectedId(item);
    if (!createForm[key].some((selected) => selectedId(selected) === id)) {
      setCreateForm({ ...createForm, [key]: [...createForm[key], id] });
      setAudienceLabels({ ...audienceLabels, [id]: selectedLabel(item) });
    }
    setAudienceSearch('');
    setAudienceResults([]);
  };

  const removeAudienceItem = (key, item) => {
    setCreateForm({
      ...createForm,
      [key]: createForm[key].filter((selected) => selectedId(selected) !== selectedId(item)),
    });
  };

  const updateTemplate = (template) => {
    const previous = TEMPLATE_CONTENT[createForm.template];
    const next = TEMPLATE_CONTENT[template];
    const canFillTitle = !createForm.title || createForm.title === previous?.title;
    const canFillMessage = !createForm.message || createForm.message === previous?.message;
    setCreateForm({
      ...createForm,
      template,
      title: next && canFillTitle ? next.title : createForm.title,
      message: next && canFillMessage ? next.message : createForm.message,
    });
  };

  const addTag = () => {
    const tags = tagInput.split(',').map((tag) => tag.trim()).filter(Boolean);
    if (tags.length) {
      setCreateForm({ ...createForm, tags: [...new Set([...createForm.tags, ...tags])] });
      setTagInput('');
    }
  };

  const handleCreateNotification = async (e, action) => {
    e.preventDefault();
    const targetKey = createForm.targetType === 'specific_users' ? 'targetUsers' : 'targetBusinesses';
    if (['specific_users', 'specific_businesses'].includes(createForm.targetType) && !createForm[targetKey].length) {
      setFormError(`Select at least one ${createForm.targetType === 'specific_users' ? 'user' : 'business'}.`);
      return;
    }

    const payload = {
      ...createForm,
      sendImmediately: action === 'send',
      scheduledAt: action === 'schedule' ? createForm.scheduledAt : null,
    };
    if (action === 'schedule' && !payload.scheduledAt) {
      setFormError('Choose a date and time before scheduling.');
      return;
    }

    try {
      if (editingNotification) {
        await httpAdmin.put(`/api/admin/notifications/${editingNotification._id}`, payload);
      } else {
        await httpAdmin.postJson('/api/admin/notifications', payload);
      }
      closeAllModals();
      setCreateForm(EMPTY_FORM);
      loadNotifications();
      loadStats();
    } catch (err) {
      if (guard(err)) return;
      setFormError(err?.data?.message || err?.message || 'Failed to save notification');
    }
  };

  const handleSendNotification = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Send notification',
      message: 'Send this notification to all targeted recipients now?',
      confirmText: 'Send',
      onConfirm: async () => {
        try {
          await httpAdmin.postJson(`/api/admin/notifications/${id}/send`);
          setConfirmDialog({ isOpen: false });
          loadNotifications();
          loadStats();
        } catch (err) {
          if (!guard(err)) setError('Failed to send notification');
        }
      },
    });
  };

  const handleCancelNotification = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cancel notification',
      message: 'Cancel this scheduled notification?',
      confirmText: 'Cancel send',
      onConfirm: async () => {
        try {
          await httpAdmin.postJson(`/api/admin/notifications/${id}/cancel`);
          setConfirmDialog({ isOpen: false });
          loadNotifications();
        } catch (err) {
          if (!guard(err)) setError('Failed to cancel notification');
        }
      },
    });
  };

  const handleDeleteNotification = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete notification',
      message: 'Are you sure you want to delete this notification? This cannot be undone.',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await httpAdmin.del(`/api/admin/notifications/${id}`);
          setConfirmDialog({ isOpen: false });
          loadNotifications();
        } catch (err) {
          if (!guard(err)) setError('Failed to delete notification');
        }
      },
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'status-draft',
      scheduled: 'status-scheduled',
      sending: 'status-sending',
      sent: 'status-sent',
      failed: 'status-failed',
      cancelled: 'status-cancelled',
    };
    return (
      <span className={`badge notif-status ${statusClasses[status] || ''}`}>
        {status}
      </span>
    );
  };

  const getTargetTypeLabel = (type) => {
    const labels = {
      all: 'All Users',
      customers: 'Customers Only',
      shop_owners: 'Shop Owners Only',
      specific_users: 'Specific Users',
      specific_businesses: 'Specific Businesses',
    };
    return labels[type] || type;
  };

  const channelSummary = (channels) => {
    if (!channels) return 'No channels';
    const active = [
      channels.email && 'Email',
      channels.sms && 'SMS',
      channels.push && 'Push',
      channels.inApp && 'In-App',
    ].filter(Boolean);
    return active.length ? active.join(' · ') : 'No channels';
  };

  return (
    <section className="adm-page notification-management">
      <header className="adm-page-head notif-page-head">
        <div className="notif-title-row">
          <h2>
            <span className="notif-title-text">Notifications</span>
            {stats?.totalNotifications != null ? (
              <span className="notif-title-count">({stats.totalNotifications})</span>
            ) : null}
          </h2>
          <button
            type="button"
            className="notif-create-btn"
            onClick={openCreateModal}
          >
            <span className="notif-create-plus" aria-hidden="true">+</span>
            Create
          </button>
        </div>
        <div className="adm-filters notif-filters">
          <div className="notif-filter-selects">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="adm-select"
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="sending">Sending</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={filters.targetType}
              onChange={(e) => setFilters({ ...filters, targetType: e.target.value, page: 1 })}
              className="adm-select"
              aria-label="Filter by target"
            >
              <option value="">All targets</option>
              <option value="all">All Users</option>
              <option value="customers">Customers</option>
              <option value="shop_owners">Shop Owners</option>
              <option value="specific_users">Specific Users</option>
              <option value="specific_businesses">Specific Businesses</option>
            </select>
          </div>
          <input
            type="text"
            placeholder="Search notifications..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="adm-search notif-search"
          />
        </div>
      </header>

      {stats && (
        <div className="notif-stats">
          <div className="notif-stat">
            <span>Notifications</span>
            <strong>{stats.totalNotifications ?? 0}</strong>
          </div>
          <div className="notif-stat">
            <span>Recipients</span>
            <strong>{stats.totalRecipients ?? 0}</strong>
          </div>
          <div className="notif-stat">
            <span>Email delivery</span>
            <strong>{stats.totalEmailSent ? `${stats.emailDeliveryRate}%` : 'No sends yet'}</strong>
          </div>
          <div className="notif-stat">
            <span>SMS delivery</span>
            <strong>{stats.totalSmsSent ? `${stats.smsDeliveryRate}%` : 'No sends yet'}</strong>
          </div>
        </div>
      )}

      <div className="adm-entity-list adm-entity-list--notifications">
        {loading ? (
          <div className="adm-loading">Loading notifications...</div>
        ) : error ? (
          <div className="adm-entity-empty">{error}</div>
        ) : notifications.length === 0 ? (
          <div className="adm-entity-empty notif-empty-state">
            <strong>No notifications found</strong>
            <span>Try changing the filters or create a notification.</span>
          </div>
        ) : (
          notifications.map((notification) => {
            const open = expandedId === notification._id;
            const initial = String(notification.title || 'N').trim().charAt(0).toUpperCase();
            return (
              <article key={notification._id} className={`adm-entity-card${open ? ' is-open' : ''}`}>
                <div
                  className="adm-entity-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpandedId(open ? null : notification._id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedId(open ? null : notification._id);
                    }
                  }}
                  aria-expanded={open}
                >
                  <div className="adm-entity-avatar" aria-hidden="true">{initial}</div>
                  <div className="adm-entity-main">
                    <div className="adm-entity-name">{notification.title || 'Untitled'}</div>
                    <div className="adm-entity-sub">
                      {getTargetTypeLabel(notification.targetType)}
                      {notification.createdAt
                        ? ` · ${new Date(notification.createdAt).toLocaleDateString()}`
                        : ''}
                    </div>
                  </div>
                  <div className="adm-entity-meta">
                    {getStatusBadge(notification.status)}
                  </div>
                  <span className="adm-entity-chevron" aria-hidden="true">{open ? '▴' : '▾'}</span>
                </div>

                {open && (
                  <div className="adm-entity-panel">
                    <div className="adm-entity-stats">
                      <div className="adm-entity-stat">
                        <span className="adm-entity-stat-label">Priority</span>
                        <strong>{notification.priority || '—'}</strong>
                      </div>
                      <div className="adm-entity-stat">
                        <span className="adm-entity-stat-label">Recipients</span>
                        <strong>{notification.stats?.totalRecipients ?? 0}</strong>
                      </div>
                      <div className="adm-entity-stat">
                        <span className="adm-entity-stat-label">Channels</span>
                        <strong>{channelSummary(notification.channels)}</strong>
                      </div>
                      <div className="adm-entity-stat">
                        <span className="adm-entity-stat-label">Created</span>
                        <strong>
                          {notification.createdAt
                            ? new Date(notification.createdAt).toLocaleDateString()
                            : '—'}
                        </strong>
                      </div>
                    </div>
                    {notification.message ? (
                      <div className="adm-entity-comment">{notification.message}</div>
                    ) : null}
                    <div className="adm-entity-actions">
                      <button
                        type="button"
                        className="btn-sm btn-info"
                        onClick={() => {
                          setShowCreateModal(false);
                          setSelectedNotification(notification);
                        }}
                      >
                        View
                      </button>
                      {notification.status === 'draft' && (
                        <button
                          type="button"
                          className="btn-sm btn-success"
                          onClick={() => handleSendNotification(notification._id)}
                        >
                          Send
                        </button>
                      )}
                      {notification.status === 'scheduled' && (
                        <button
                          type="button"
                          className="btn-sm btn-warning"
                          onClick={() => handleCancelNotification(notification._id)}
                        >
                          Cancel
                        </button>
                      )}
                      {['draft', 'scheduled', 'failed', 'cancelled'].includes(notification.status) && (
                        <button
                          type="button"
                          className="btn-sm btn-info"
                          onClick={() => openEditModal(notification)}
                        >
                          Edit
                        </button>
                      )}
                      {['draft', 'scheduled', 'failed', 'cancelled'].includes(notification.status) && (
                        <button
                          type="button"
                          className="btn-sm btn-danger"
                          onClick={() => handleDeleteNotification(notification._id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>
      <PaginationBar
        page={filters.page}
        pageSize={filters.limit}
        total={total}
        onPageChange={(page) => setFilters({ ...filters, page })}
      />

      {showCreateModal && (
        <div className="modal-overlay notif-modal-overlay" onClick={closeAllModals}>
          <div
            className="modal-card notif-modal-card"
            role="dialog"
            aria-labelledby="notif-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-topbar notif-modal-topbar notif-create-topbar">
              <div className="notif-create-heading">
                <h3 id="notif-create-title">{editingNotification ? 'Edit Notification' : 'Create Notification'}</h3>
                <p className="notif-modal-sub">Write the message, choose an audience, then save, schedule, or send.</p>
              </div>
              <button
                type="button"
                className="notif-close-btn"
                onClick={closeAllModals}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={(e) => handleCreateNotification(e, createForm.sendImmediately ? 'send' : 'draft')} className="create-form notif-create-form">
              <section className="notif-form-section">
                <h4 className="notif-form-section-title">Content</h4>
                <div className="notif-form-grid">
                  <div className="form-group">
                    <label htmlFor="notif-template">Template</label>
                    <select id="notif-template" value={createForm.template} onChange={(e) => updateTemplate(e.target.value)}>
                      <option value="custom">Custom</option>
                      <option value="booking_reminder">Booking reminder</option>
                      <option value="promotion">Promotion</option>
                      <option value="system_update">System update</option>
                      <option value="welcome">Welcome</option>
                      <option value="password_reset">Password reset</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="notif-tags">Tags</label>
                    <div className="notif-tag-input">
                      <input id="notif-tags" value={tagInput} placeholder="vip, offer" onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
                      <button type="button" onClick={addTag}>Add</button>
                    </div>
                  </div>
                </div>
                {createForm.tags.length > 0 && <div className="notif-tags">{createForm.tags.map((tag) => <button key={tag} type="button" onClick={() => setCreateForm({ ...createForm, tags: createForm.tags.filter((value) => value !== tag) })}>{tag} ×</button>)}</div>}
                <div className="form-group">
                  <label htmlFor="notif-title">Title</label>
                  <input
                    id="notif-title"
                    type="text"
                    required
                    placeholder="Short clear title"
                    value={createForm.title}
                    onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="notif-message">Message</label>
                  <textarea
                    id="notif-message"
                    required
                    rows={4}
                    placeholder="What should recipients know?"
                    value={createForm.message}
                    onChange={(e) => setCreateForm({ ...createForm, message: e.target.value })}
                  />
                </div>
              </section>

              <section className="notif-form-section">
                <h4 className="notif-form-section-title">Audience</h4>
                <div className="notif-form-grid">
                  <div className="form-group">
                    <label htmlFor="notif-target">Target</label>
                    <select
                      id="notif-target"
                      value={createForm.targetType}
                      onChange={(e) => setCreateForm({ ...createForm, targetType: e.target.value })}
                    >
                      <option value="all">All Users</option>
                      <option value="customers">Customers Only</option>
                      <option value="shop_owners">Shop Owners Only</option>
                      <option value="specific_users">Specific Users</option>
                      <option value="specific_businesses">Specific Businesses</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="notif-priority">Priority</label>
                    <select
                      id="notif-priority"
                      value={createForm.priority}
                      onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
                {['specific_users', 'specific_businesses'].includes(createForm.targetType) && (
                  <div className="notif-audience-picker">
                    <label htmlFor="notif-audience-search">Select {createForm.targetType === 'specific_users' ? 'users' : 'businesses'}</label>
                    <input id="notif-audience-search" value={audienceSearch} onChange={(e) => setAudienceSearch(e.target.value)} placeholder={`Search ${createForm.targetType === 'specific_users' ? 'users' : 'businesses'}...`} />
                    <div className="notif-selected-items">
                      {createForm[createForm.targetType === 'specific_users' ? 'targetUsers' : 'targetBusinesses'].map((item) => (
                        <button type="button" className="notif-selected-chip" key={selectedId(item)} onClick={() => removeAudienceItem(createForm.targetType === 'specific_users' ? 'targetUsers' : 'targetBusinesses', item)}>{audienceLabels[selectedId(item)] || selectedLabel(item)} ×</button>
                      ))}
                    </div>
                    {audienceResults.length > 0 && <div className="notif-audience-results">{audienceResults.map((item) => <button type="button" key={item._id} onClick={() => addAudienceItem(item)}>{selectedLabel(item)}{item.email ? ` · ${item.email}` : ''}</button>)}</div>}
                  </div>
                )}
              </section>

              <section className="notif-form-section">
                <h4 className="notif-form-section-title">Channels</h4>
                <div className="form-group">
                  <div className="channel-options notif-channel-options">
                    {['email', 'sms', 'push', 'inApp'].map((ch) => (
                      <label key={ch} className={`notif-channel-chip${createForm.channels[ch] ? ' is-on' : ''}`}>
                        <input
                          type="checkbox"
                          checked={createForm.channels[ch]}
                          onChange={(e) =>
                            setCreateForm({
                              ...createForm,
                              channels: { ...createForm.channels, [ch]: e.target.checked },
                            })
                          }
                        />
                        <span>{ch === 'inApp' ? 'In-App' : ch.toUpperCase()}</span>
                      </label>
                    ))}
                  </div>
                </div>

              </section>

              <section className="notif-form-section">
                <h4 className="notif-form-section-title">Schedule</h4>
                <div className="notif-schedule-row">
                  <label className={`notif-send-toggle${createForm.sendImmediately ? ' is-on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={createForm.sendImmediately}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, sendImmediately: e.target.checked })
                      }
                    />
                    <span>Send immediately</span>
                  </label>

                  {!createForm.sendImmediately && (
                    <div className="form-group notif-schedule-field">
                      <label htmlFor="notif-schedule">Schedule date & time</label>
                      <input
                        id="notif-schedule"
                        type="datetime-local"
                        value={createForm.scheduledAt}
                        onChange={(e) => setCreateForm({ ...createForm, scheduledAt: e.target.value })}
                      />
                    </div>
                  )}
                </div>
              </section>

              <div className="form-actions notif-form-actions">
                <button type="button" className="btn-cancel" onClick={closeAllModals}>
                  Cancel
                </button>
                {!createForm.sendImmediately && (
                  <>
                    <button type="button" className="btn-cancel" onClick={(e) => handleCreateNotification(e, 'draft')}>
                      Save draft
                    </button>
                    <button type="button" className="btn-create" onClick={(e) => handleCreateNotification(e, 'schedule')}>
                      Save & schedule
                    </button>
                  </>
                )}
                {createForm.sendImmediately && (
                  <button type="submit" className="btn-create">
                    {editingNotification ? 'Save & send' : 'Create & send'}
                  </button>
                )}
                {formError && <p className="notif-form-error" role="alert">{formError}</p>}
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedNotification && (
        <div className="modal-overlay notif-modal-overlay" onClick={closeAllModals}>
          <div
            className="modal-card notif-modal-card"
            role="dialog"
            aria-labelledby="notif-details-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-topbar notif-modal-topbar">
              <div>
                <h3 id="notif-details-title">Notification Details</h3>
                <p className="notif-modal-sub">{selectedNotification.title}</p>
              </div>
              <button
                type="button"
                className="notif-close-btn"
                onClick={closeAllModals}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="details-grid notif-details-grid">
              <section className="notif-form-section">
                <h4 className="notif-form-section-title">Content</h4>
                <p>
                  <strong>Message:</strong> {selectedNotification.message}
                </p>
                <p>
                  <strong>Status:</strong> {getStatusBadge(selectedNotification.status)}
                </p>
                <p>
                  <strong>Priority:</strong> {selectedNotification.priority}
                </p>
              </section>

              <section className="notif-form-section">
                <h4 className="notif-form-section-title">Audience</h4>
                <p>
                  <strong>Type:</strong> {getTargetTypeLabel(selectedNotification.targetType)}
                </p>
                <p>
                  <strong>Recipients:</strong> {selectedNotification.stats?.totalRecipients || 0}
                </p>
              </section>

              <section className="notif-form-section">
                <h4 className="notif-form-section-title">Channels</h4>
                <div className="detail-channels">
                  {selectedNotification.channels?.email && <span>Email</span>}
                  {selectedNotification.channels?.sms && <span>SMS</span>}
                  {selectedNotification.channels?.push && <span>Push</span>}
                  {selectedNotification.channels?.inApp && <span>In-App</span>}
                </div>
              </section>
              <section className="notif-form-section">
                <h4 className="notif-form-section-title">Schedule</h4>
                <p><strong>Scheduled:</strong> {selectedNotification.scheduledAt ? new Date(selectedNotification.scheduledAt).toLocaleString() : 'Not scheduled'}</p>
              </section>
            </div>
            {['draft', 'scheduled', 'failed', 'cancelled'].includes(selectedNotification.status) && (
              <div className="notif-details-actions">
                <button type="button" className="btn-create" onClick={() => openEditModal(selectedNotification)}>Edit notification</button>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
      />
    </section>
  );
};

export default NotificationManagement;
